import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Icon, V2Badge } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { ApiError, apiPost } from '../../lib/apiClient.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import { UserAvatar } from '../../lib/avatars.js';
import { dueTone, exactWhen, relativeWhen } from '../../lib/date/relativeWhen.js';
import { faDigits } from '../../lib/faDigits.js';
import { Pagination } from '../../components/list/Pagination.js';
import { RowMenu } from '../../components/list/RowMenu.js';
import type {
  LeadBoardResponse,
  LeadDetail,
  LeadListResponse,
  LeadOptionsResponse,
  LeadRow,
} from '../../lib/types/crm.js';
import { LEAD_STATUS_OPTIONS } from '../../lib/types/crm.js';
import { LeadBoard } from './leads/LeadBoard.js';
import { LeadDetailPanel, type LeadPanelMode } from './leads/LeadDetailPanel.js';
import { LeadFormPanel } from './leads/LeadFormPanel.js';
import {
  LEAD_PRIORITY_TONE,
  LEAD_QUICK_VIEWS,
  LEAD_STATUS_TONE,
  isOpenLead,
  formatMasked,
  type LeadQuickView,
} from './leads/leadUi.js';

const PAGE_SIZE = 20;

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Sales CRM.dc.html §02 LEADS (list, 520px detail panel,
 *   validation + duplicate form) and §03 PIPELINE (kanban board) — the
 *   separate «فرصت فروش» screen is folded into this one: a lead carries the
 *   deal (stage, amount, expected close, priority) and is won or lost here.
 * archetype: ENTITY_LIST (list ⇄ kanban board) + side-panel detail and form
 * status: REAL — every column is backed by GET /crm/leads (record-scoped,
 *   server-paginated): masked contact (full value only via the audited
 *   reveal in the detail), owner name/avatar, last activity and next action
 *   computed from real activities. Filters, quick view and page live in
 *   the URL so they survive opening a lead and coming back.
 * reconciliation:
 *   - Labels sit above their controls (product spec) rather than the
 *     board's 110px side labels, which do not fit the 390px layout.
 *   - Statuses are the backend's five (جدید · تماس گرفته‌شده · واجد شرایط ·
 *     تبدیل‌شده · رد شده), not the board's four — collapsing two real
 *     states into «در حال پیگیری» would lose data on filter.
 *   - «تبدیل به طرف‌حساب» stays disabled with the board's own reason.
 * state reference: ready · loading · empty (none yet / no match) ·
 *   permission_denied · network_error
 */
export function LeadsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [params, setParams] = useSearchParams();

  const { unit, label: unitLabel } = useAmountDisplay();
  const layout = params.get('layout') === 'list' ? 'list' : 'board';
  const view = (params.get('view') as LeadQuickView | null) ?? 'all';
  const status = params.get('status') ?? '';
  const stageFilter = params.get('stage') ?? '';
  const priorityFilter = params.get('priority') ?? '';
  const source = params.get('source') ?? '';
  const search = params.get('q') ?? '';
  const sort = params.get('sort') ?? '';
  const offset = Number(params.get('offset') ?? '0') || 0;
  const [searchInput, setSearchInput] = useState(search);

  const [refreshKey, setRefreshKey] = useState(0);
  const [openRef, setOpenRef] = useState<string | null>(params.get('lead'));
  const [openMode, setOpenMode] = useState<LeadPanelMode>('view');
  const presetParty = params.get('customer') ?? undefined;
  const [formOpen, setFormOpen] = useState(params.get('new') === '1');
  const [editing, setEditing] = useState<LeadDetail | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [busyRef, setBusyRef] = useState<string | null>(null);

  function update(next: Record<string, string | null>, resetPage = true) {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value === null || value === '') p.delete(key);
          else p.set(key, value);
        }
        if (resetPage) p.delete('offset');
        return p;
      },
      { replace: true },
    );
  }

  // Search runs after a short pause, not on every keystroke.
  useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => update({ q: searchInput.trim() || null }), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (search) q.set('search', search);
    const effectiveStatus =
      view === 'converted' ? 'converted' : view === 'disqualified' ? 'disqualified' : status;
    if (effectiveStatus) q.set('status', effectiveStatus);
    if (view === 'open') q.set('view', 'open');
    if (source) q.set('sourceLabel', source);
    if (stageFilter) q.set('stage', stageFilter);
    if (priorityFilter) q.set('priority', priorityFilter);
    if (view === 'mine') q.set('owner', 'me');
    if (view === 'followup') q.set('followUp', 'overdue');
    if (sort) q.set('sort', sort);
    q.set('offset', String(offset));
    q.set('limit', String(PAGE_SIZE));
    return q.toString();
  }, [search, status, source, view, sort, offset, stageFilter, priorityFilter]);

  const list = useApiGet<LeadListResponse>(
    slug && layout === 'list' ? `${base}/api/v1/crm/leads?${query}` : null,
    [slug, query, refreshKey, layout],
  );
  const boardQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (search) q.set('search', search);
    if (priorityFilter) q.set('priority', priorityFilter);
    if (view === 'mine') q.set('owner', 'me');
    return q.toString();
  }, [search, priorityFilter, view]);
  const board = useApiGet<LeadBoardResponse>(
    slug && layout === 'board' ? `${base}/api/v1/crm/leads/board?${boardQuery}` : null,
    [slug, boardQuery, refreshKey, layout],
  );
  // Stage names for the filter; needs create/update, so a read-only user falls back to the board's.
  const options = useApiGet<LeadOptionsResponse>(slug ? `${base}/api/v1/crm/leads/options` : null, [
    slug,
  ]);
  const stageOptions =
    options.status === 'ready'
      ? ((options.data.pipelines.find((p) => p.isDefault) ?? options.data.pipelines[0])?.stages ??
        [])
      : board.status === 'ready'
        ? board.data.stages
        : [];

  const filtered = Boolean(
    search || status || source || stageFilter || priorityFilter || view !== 'all',
  );

  async function moveLead(lead: LeadRow, stageRef: string) {
    setMoveError(null);
    setBusyRef(lead.ref);
    try {
      await apiPost(`${base}/api/v1/crm/leads/${lead.ref}/stage`, { stageRef });
      setToast(`«${lead.displayName}» به مرحلهٔ بعد منتقل شد.`);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setMoveError(
        error instanceof ApiError && error.status === 403
          ? 'اجازهٔ تغییر مرحلهٔ این سرنخ را ندارید.'
          : 'انتقال انجام نشد؛ فقط حرکت به مرحلهٔ بعدی مجاز است.',
      );
    } finally {
      setBusyRef(null);
    }
  }
  const chips: { key: string; label: string; clear: () => void }[] = [
    ...(search
      ? [
          {
            key: 'q',
            label: `جست‌وجو: ${search}`,
            clear: () => {
              setSearchInput('');
              update({ q: null });
            },
          },
        ]
      : []),
    ...(status
      ? [
          {
            key: 'status',
            label: `وضعیت: ${LEAD_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status}`,
            clear: () => update({ status: null }),
          },
        ]
      : []),
    ...(source
      ? [{ key: 'source', label: `منبع: ${source}`, clear: () => update({ source: null }) }]
      : []),
    ...(stageFilter
      ? [
          {
            key: 'stage',
            label: `مرحله: ${stageOptions.find((st) => st.ref === stageFilter)?.name ?? stageFilter}`,
            clear: () => update({ stage: null }),
          },
        ]
      : []),
    ...(priorityFilter
      ? [{ key: 'priority', label: 'اولویت انتخاب‌شده', clear: () => update({ priority: null }) }]
      : []),
  ];

  function openLead(ref: string, mode: LeadPanelMode = 'view') {
    setOpenMode(mode);
    setOpenRef(ref);
    update({ lead: ref }, false);
  }
  function closeLead() {
    setOpenRef(null);
    update({ lead: null }, false);
  }

  const rows = list.status === 'ready' ? list.data.leads : [];
  const total = list.status === 'ready' ? list.data.total : 0;
  const boardTotal =
    board.status === 'ready' ? board.data.stages.reduce((sum, st) => sum + st.count, 0) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{LEADS_CSS}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--sk-fg)' }}>
            سرنخ‌ها
          </h2>
          <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            {layout === 'list'
              ? list.status === 'ready'
                ? `${faDigits(total)} سرنخ در دسترس شما`
                : '\u00a0'
              : board.status === 'ready'
                ? `${faDigits(boardTotal)} سرنخ در جریان — ${board.data.pipelineName}`
                : '\u00a0'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div role="group" aria-label="نحوهٔ نمایش" style={{ display: 'flex', gap: 4 }}>
            {(
              [
                ['board', 'قیف فروش', 'layers'],
                ['list', 'فهرست', 'menu'],
              ] as const
            ).map(([id, text, icon]) => (
              <button
                key={id}
                type="button"
                aria-pressed={layout === id}
                className={`sk-btn ${layout === id ? 'sk-btn--secondary' : 'sk-btn--ghost'}`}
                style={
                  layout === id
                    ? { color: 'var(--sk-brand-600)', borderColor: 'var(--sk-brand-300)' }
                    : undefined
                }
                onClick={() => update({ layout: id === 'board' ? null : 'list' }, false)}
              >
                <Icon name={icon} size="sm" />
                {text}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="sk-btn sk-btn--primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Icon name="plus" size="sm" />
            سرنخ جدید
          </button>
          <Link
            to={`${base}/data`}
            className="sk-btn sk-btn--secondary"
            style={{ textDecoration: 'none' }}
          >
            <Icon name="upload" size="sm" />
            ورود از فایل
          </Link>
        </div>
      </div>

      {moveError ? (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-danger-soft)',
            fontSize: 13,
          }}
        >
          {moveError}
        </div>
      ) : null}
      {toast ? (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-success-soft)',
            fontSize: 13,
          }}
        >
          {toast}
        </div>
      ) : null}

      <div className="sk-card" style={{ overflow: 'visible' }}>
        <div
          role="tablist"
          aria-label="نمای سریع"
          style={{ display: 'flex', gap: 6, padding: '12px 16px 0', flexWrap: 'wrap' }}
        >
          {LEAD_QUICK_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={`sk-btn sk-btn--sm ${view === v.id ? 'sk-btn--secondary' : 'sk-btn--ghost'}`}
              style={
                view === v.id
                  ? { color: 'var(--sk-brand-600)', borderColor: 'var(--sk-brand-300)' }
                  : undefined
              }
              onClick={() =>
                update({
                  view: v.id === 'all' ? null : v.id,
                  status: v.id === 'converted' || v.id === 'disqualified' ? null : status || null,
                })
              }
            >
              {v.label}
            </button>
          ))}
        </div>
        <div
          className="sk-leads-filters"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: '1px solid var(--sk-divider)',
            flexWrap: 'wrap',
          }}
        >
          <div className="sk-input-group" style={{ width: 260 }}>
            <Icon name="search" size="sm" />
            <input
              aria-label="جست‌وجو در نام، موضوع نیاز، شماره یا کد"
              placeholder="نام، موضوع نیاز یا شماره"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {layout === 'list' ? (
            <select
              className="sk-select"
              aria-label="وضعیت"
              style={{ width: 150 }}
              value={
                view === 'converted'
                  ? 'converted'
                  : view === 'disqualified'
                    ? 'disqualified'
                    : status
              }
              disabled={view === 'converted' || view === 'disqualified'}
              onChange={(e) => update({ status: e.target.value || null })}
            >
              <option value="">همهٔ وضعیت‌ها</option>
              {LEAD_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : null}
          {layout === 'list' && stageOptions.length > 0 ? (
            <select
              className="sk-select"
              aria-label="مرحله"
              style={{ width: 150 }}
              value={stageFilter}
              onChange={(e) => update({ stage: e.target.value || null })}
            >
              <option value="">همهٔ مراحل</option>
              {stageOptions.map((st) => (
                <option key={st.ref} value={st.ref}>
                  {st.name}
                </option>
              ))}
            </select>
          ) : null}
          <select
            className="sk-select"
            aria-label="اولویت"
            style={{ width: 130 }}
            value={priorityFilter}
            onChange={(e) => update({ priority: e.target.value || null })}
          >
            <option value="">همهٔ اولویت‌ها</option>
            <option value="urgent">فوری</option>
            <option value="high">بالا</option>
            <option value="normal">عادی</option>
            <option value="low">کم</option>
          </select>
          {layout === 'list' ? (
            <>
              <select
                className="sk-select"
                aria-label="منبع"
                style={{ width: 150 }}
                value={source}
                onChange={(e) => update({ source: e.target.value || null })}
              >
                <option value="">همهٔ منابع</option>
                {(list.status === 'ready' ? list.data.sourceOptions : []).map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </select>
              <select
                className="sk-select"
                aria-label="مرتب‌سازی"
                style={{ width: 170 }}
                value={sort}
                onChange={(e) => update({ sort: e.target.value || null })}
              >
                <option value="">جدیدترین</option>
                <option value="amount-desc">بیشترین مبلغ</option>
                <option value="close-asc">نزدیک‌ترین تاریخ بستن</option>
                <option value="nextAction">نزدیک‌ترین پیگیری</option>
                <option value="name">نام (الفبایی)</option>
              </select>
            </>
          ) : null}
          {filtered ? (
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--sm"
              onClick={() => {
                setSearchInput('');
                update({
                  q: null,
                  status: null,
                  source: null,
                  stage: null,
                  priority: null,
                  view: null,
                  sort: null,
                });
              }}
            >
              پاک‌کردن فیلترها
            </button>
          ) : null}
        </div>
        {chips.length > 0 ? (
          <div style={{ display: 'flex', gap: 6, padding: '10px 16px 0', flexWrap: 'wrap' }}>
            {chips.map((chip) => (
              <span
                key={chip.key}
                className="sk-tag"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {chip.label}
                <button
                  type="button"
                  aria-label={`حذف فیلتر ${chip.label}`}
                  onClick={chip.clear}
                  style={{
                    border: 0,
                    background: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    color: 'inherit',
                  }}
                >
                  <Icon name="x" size="sm" />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {layout === 'board' ? (
          board.status === 'loading' ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              در حال بارگذاری قیف فروش…
            </div>
          ) : board.status === 'error' ? (
            <EmptyBlock
              title={
                board.error.status === 403
                  ? 'به سرنخ‌ها دسترسی ندارید'
                  : 'بارگذاری قیف فروش انجام نشد'
              }
              body={
                board.error.status === 403
                  ? 'برای دسترسی با مدیر سازمان هماهنگ کنید.'
                  : 'اتصال را بررسی کنید و دوباره تلاش کنید.'
              }
              action={
                board.error.status === 403 ? null : (
                  <button
                    type="button"
                    className="sk-btn sk-btn--secondary sk-btn--sm"
                    onClick={() => setRefreshKey((k) => k + 1)}
                  >
                    تلاش مجدد
                  </button>
                )
              }
            />
          ) : boardTotal === 0 ? (
            <EmptyBlock
              title={filtered ? 'نتیجه‌ای برای این فیلترها نیست' : 'هنوز سرنخی در جریان نیست'}
              body={
                filtered
                  ? 'فیلترها را تغییر دهید یا پاک کنید.'
                  : 'اولین سرنخ را ثبت کنید تا در قیف فروش دیده شود.'
              }
              action={
                <button
                  type="button"
                  className="sk-btn sk-btn--primary sk-btn--sm"
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  سرنخ جدید
                </button>
              }
            />
          ) : (
            <LeadBoard
              board={board.data}
              unit={unit}
              unitLabel={unitLabel}
              busyRef={busyRef}
              onOpen={(ref) => openLead(ref)}
              onMove={(lead, stageRef) => void moveLead(lead, stageRef)}
            />
          )
        ) : (
          <>
            {list.status === 'loading' ? (
              <div style={{ padding: 24, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                در حال بارگذاری سرنخ‌ها…
              </div>
            ) : list.status === 'error' ? (
              <EmptyBlock
                title={
                  list.error.status === 403
                    ? 'به سرنخ‌ها دسترسی ندارید'
                    : 'بارگذاری سرنخ‌ها انجام نشد'
                }
                body={
                  list.error.status === 403
                    ? 'برای دسترسی با مدیر سازمان هماهنگ کنید.'
                    : 'اتصال را بررسی کنید و دوباره تلاش کنید.'
                }
                action={
                  list.error.status === 403 ? null : (
                    <button
                      type="button"
                      className="sk-btn sk-btn--secondary sk-btn--sm"
                      onClick={() => setRefreshKey((k) => k + 1)}
                    >
                      تلاش مجدد
                    </button>
                  )
                }
              />
            ) : rows.length === 0 ? (
              filtered ? (
                <EmptyBlock
                  title="نتیجه‌ای برای این فیلترها نیست"
                  body="فیلترها را تغییر دهید یا پاک کنید."
                  action={
                    <button
                      type="button"
                      className="sk-btn sk-btn--secondary sk-btn--sm"
                      onClick={() => {
                        setSearchInput('');
                        update({ q: null, status: null, source: null, view: null });
                      }}
                    >
                      پاک‌کردن فیلترها
                    </button>
                  }
                />
              ) : (
                <EmptyBlock
                  title="هنوز سرنخی ثبت نشده"
                  body="اولین سرنخ را ثبت کنید یا فهرست را از فایل وارد کنید."
                  action={
                    <button
                      type="button"
                      className="sk-btn sk-btn--primary sk-btn--sm"
                      onClick={() => {
                        setEditing(null);
                        setFormOpen(true);
                      }}
                    >
                      سرنخ جدید
                    </button>
                  }
                />
              )
            ) : (
              <>
                <div className="sk-leads-table-wrap sk-table-scroll">
                  <table className="sk-table sk-leads-table">
                    <thead>
                      <tr>
                        <th>نام / موضوع نیاز</th>
                        <th style={{ width: 140 }} className="sk-col-lg">
                          شمارهٔ تماس
                        </th>
                        <th style={{ width: 150 }}>مرحله</th>
                        <th style={{ width: 150 }}>مبلغ ({unitLabel})</th>
                        <th style={{ width: 120 }} className="sk-col-md">
                          تاریخ بستن
                        </th>
                        <th style={{ width: 120 }}>وضعیت</th>
                        <th style={{ width: 150 }} className="sk-col-md">
                          مسئول
                        </th>
                        <th style={{ width: 190 }} className="sk-col-lg">
                          اقدام بعدی
                        </th>
                        <th style={{ width: 48 }}>
                          <span className="sk-visually-hidden">عملیات</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr
                          key={row.ref}
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openLead(row.ref)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') openLead(row.ref);
                          }}
                        >
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                minWidth: 0,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 500,
                                  color:
                                    row.status === 'disqualified'
                                      ? 'var(--sk-fg-muted)'
                                      : 'var(--sk-fg)',
                                }}
                              >
                                {row.displayName}
                              </span>
                              <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                                {row.deal.customer ? (
                                  <>
                                    مشتری:{' '}
                                    <PartyLink base={base} partyRef={row.deal.customer.ref} inRow>
                                      {row.deal.customer.label}
                                    </PartyLink>
                                  </>
                                ) : (
                                  [row.leadKindLabel, row.needSubject]
                                    .filter(Boolean)
                                    .join(' · ') || row.ref
                                )}
                              </span>
                            </div>
                          </td>
                          <td
                            className="sk-col-lg"
                            dir="ltr"
                            style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
                          >
                            {row.contact.mobileMasked
                              ? formatMasked(row.contact.mobileMasked)
                              : row.contact.phoneMasked
                                ? formatMasked(row.contact.phoneMasked)
                                : (row.contact.altContactLabel ?? '—')}
                          </td>
                          <td>
                            {isOpenLead(row.status) && row.deal.stage ? (
                              <span className="sk-tag">
                                {row.deal.stage.name} · {faDigits(row.deal.probabilityPct)}٪
                              </span>
                            ) : (
                              <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
                            )}
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatRialFa(row.deal.amountRial, unit)}
                          </td>
                          <td className="sk-col-md">
                            {row.deal.expectedCloseDate
                              ? displayJalaliDate(row.deal.expectedCloseDate)
                              : '—'}
                          </td>
                          <td>
                            <V2Badge tone={LEAD_STATUS_TONE[row.status]}>{row.statusLabel}</V2Badge>
                          </td>
                          <td className="sk-col-md">
                            {row.owner ? (
                              <span
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                              >
                                <UserAvatar
                                  name={row.owner.name}
                                  source={{
                                    avatarPreset: row.owner.avatarPreset,
                                    avatarPhotoDataUrl: null,
                                  }}
                                  size={24}
                                />
                                <span
                                  style={{
                                    color: row.owner.active ? undefined : 'var(--sk-fg-muted)',
                                  }}
                                >
                                  {row.owner.name}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: 'var(--sk-warn)' }}>بدون مسئول</span>
                            )}
                          </td>
                          <td
                            className="sk-col-lg"
                            title={
                              row.nextAction?.dueAt ? exactWhen(row.nextAction.dueAt) : undefined
                            }
                          >
                            <NextAction row={row} />
                          </td>
                          <td>
                            <RowMenu
                              items={[
                                { label: 'مشاهدهٔ جزئیات', onSelect: () => openLead(row.ref) },
                                ...(isOpenLead(row.status)
                                  ? [
                                      {
                                        label: 'ثبت تماس',
                                        onSelect: () => openLead(row.ref, 'call'),
                                      },
                                      {
                                        label: 'انتقال به مرحلهٔ بعد',
                                        onSelect: () => openLead(row.ref, 'stage'),
                                      },
                                      {
                                        label: 'برنده شد',
                                        onSelect: () => openLead(row.ref, 'win'),
                                      },
                                      {
                                        label: 'ویرایش',
                                        onSelect: () => openLead(row.ref, 'edit'),
                                      },
                                      {
                                        label: 'از دست رفت',
                                        danger: true,
                                        onSelect: () => openLead(row.ref, 'lose'),
                                      },
                                    ]
                                  : []),
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sk-leads-cards">
                  {rows.map((row) => (
                    <button
                      key={row.ref}
                      type="button"
                      className="sk-card"
                      onClick={() => openLead(row.ref)}
                      style={{
                        padding: 14,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        textAlign: 'start',
                        width: '100%',
                        background:
                          row.status === 'disqualified' ? 'var(--sk-bg-canvas)' : undefined,
                        cursor: 'pointer',
                        font: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          width: '100%',
                        }}
                      >
                        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <span style={{ fontSize: 15, fontWeight: 600 }}>{row.displayName}</span>
                          <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                            {formatRialFa(row.deal.amountRial, unit)} {unitLabel}
                          </span>
                        </span>
                        <V2Badge tone={LEAD_STATUS_TONE[row.status]}>{row.statusLabel}</V2Badge>
                      </span>
                      <span
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          paddingTop: 9,
                          borderTop: '1px solid var(--sk-divider)',
                          fontSize: 12,
                          color: 'var(--sk-fg-muted)',
                          width: '100%',
                        }}
                      >
                        {row.status === 'disqualified' && row.deal.lostReason ? (
                          <span>دلیل از دست رفتن: {row.deal.lostReason.label}</span>
                        ) : (
                          <>
                            {isOpenLead(row.status) && row.deal.stage ? (
                              <span>
                                مرحله: {row.deal.stage.name}{' '}
                                <V2Badge tone={LEAD_PRIORITY_TONE[row.deal.priority]}>
                                  {row.deal.priorityLabel}
                                </V2Badge>
                              </span>
                            ) : null}
                            <span>
                              <NextAction row={row} />
                            </span>
                            <span>مسئول: {row.owner?.name ?? 'بدون مسئول'}</span>
                          </>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
                <Pagination
                  offset={offset}
                  limit={PAGE_SIZE}
                  total={total}
                  onOffset={(o) => update({ offset: o ? String(o) : null }, false)}
                />
              </>
            )}
          </>
        )}
      </div>

      <LeadDetailPanel
        base={base}
        leadRef={openRef}
        initialMode={openMode}
        refreshKey={refreshKey}
        onClose={closeLead}
        onEdit={(lead) => {
          setEditing(lead);
          setFormOpen(true);
        }}
        onChanged={(message) => {
          setToast(message);
          setRefreshKey((k) => k + 1);
        }}
      />
      <LeadFormPanel
        base={base}
        open={formOpen}
        editing={editing}
        presetPartyRef={presetParty}
        onClose={() => {
          setFormOpen(false);
          if (params.get('new')) update({ new: null, customer: null }, false);
        }}
        onOpenLead={(ref) => {
          setFormOpen(false);
          openLead(ref);
        }}
        onSaved={(lead, keepOpen) => {
          setRefreshKey((k) => k + 1);
          setToast(editing ? 'تغییرات سرنخ ذخیره شد.' : `سرنخ «${lead.title}» ثبت شد.`);
          if (!keepOpen) {
            setFormOpen(false);
            if (!editing) openLead(lead.ref);
          }
        }}
      />
    </div>
  );
}

function NextAction({ row }: { row: LeadRow }) {
  if (row.status === 'converted' || row.status === 'disqualified') {
    return <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>;
  }
  if (!row.nextAction) {
    return <span style={{ color: 'var(--sk-warn)' }}>بدون اقدام تعیین‌شده</span>;
  }
  const tone = dueTone(row.nextAction.dueAt);
  return (
    <span
      style={{
        color:
          tone === 'overdue'
            ? 'var(--sk-danger)'
            : tone === 'today'
              ? 'var(--sk-fg)'
              : 'var(--sk-fg-muted)',
      }}
    >
      {row.nextAction.typeLabel}
      {row.nextAction.dueAt ? ` — ${relativeWhen(row.nextAction.dueAt)}` : ''}
      {tone === 'overdue' ? ' · عقب‌افتاده' : ''}
    </span>
  );
}

function EmptyBlock({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <div className="sk-empty" style={{ padding: '32px 16px' }}>
      <div className="sk-empty__title">{title}</div>
      <div className="sk-empty__desc">{body}</div>
      {action ? <div style={{ marginTop: 6 }}>{action}</div> : null}
    </div>
  );
}

const LEADS_CSS = `
.sk-leads-table-wrap { overflow-x: auto; }
.sk-leads-table .sk-tag { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: middle; }
.sk-leads-table tbody tr:focus-visible { outline: 2px solid var(--sk-brand-500); outline-offset: -2px; }
.sk-leads-cards { display: none; }
.sk-visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (max-width: 1180px) { .sk-leads-table .sk-col-lg { display: none; } }
@media (max-width: 1020px) { .sk-leads-table .sk-col-md { display: none; } }
@media (max-width: 899px) {
  .sk-leads-table-wrap { display: none; }
  .sk-leads-cards { display: flex; flex-direction: column; gap: 10px; padding: 12px; }
  .sk-leads-filters .sk-input-group { width: 100% !important; }
  .sk-leads-filters .sk-select { flex: 1 1 140px; width: auto !important; }
}
`;
